import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';

import Messages from "../../../constants/Messages";
import uRLs from "../../../utils/baseUtils";
import { useShowAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthProvider";
import { useNetwork } from "../../../context/NetworkContext";
import { ChevronLeftIcon } from "../../../constants/Icons";

const UpdatePassword = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { checkNetwork } = useNetwork();
    const showAlert = useShowAlert();

    const accessToken = location.state?.accessToken;
    const isfrom = location.state?.isfrom;
    const successRedirect = location.state?.successRedirect;
    const failureRedirect = location.state?.failureRedirect;

    const [isResetData, setResetData] = useState({ email: '' });
    const passwordInputRef = useRef(null);
    const repasswordInputRef = useRef(null);
    const [isBtnLoading, setBtnLoading] = useState(false);
    const [error, setErrors] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);

    const [showPassword, setShowPassword] = useState(false);
    const [showRePassword, setShowRePassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const cleanedValue = (name === 'email') ? value.replace(/\s/g, '').trim() : value;
        const updatedData = {
            ...isResetData,
            [name]: cleanedValue,
        };
        setResetData(updatedData);
    };

    const validateForm = () => {
        const errors = {};
        if (!isResetData.password) {
            errors.password = Messages.ERROR.PASSWORD_EMPTY;
        } else if (isResetData.password.length < 8) {
            errors.password = Messages.ERROR.PASSWORD_TOO_SHORT;
        } else if (!/\d/.test(isResetData.password)) {
            errors.password = Messages.ERROR.PASSWORD_NO_NUMBER;
        } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(isResetData.password)) {
            errors.password = Messages.ERROR.PASSWORD_NO_SPECIAL;
        } else if (!isResetData.repassword) {
            errors.repassword = Messages.ERROR.REPASSWORD_EMPTY;
        } else if (isResetData.password !== isResetData.repassword) {
            errors.repassword = Messages.ERROR.PASSWORD_MISMATCH;
        }
        setErrors(errors);
        if (Object.keys(errors).length > 0) {
            const firstErrorField = Object.keys(errors)[0];
            switch (firstErrorField) {
                case "password":
                    passwordInputRef.current?.focus();
                    break;
                case "repassword":
                    repasswordInputRef.current?.focus();
                    break;
                default:
                    break;
            }
        }
        return Object.keys(errors).length === 0;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleReset();
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleRePasswordVisibility = () => {
        setShowRePassword(!showRePassword);
    };

    const handleReset = () => {
        setErrorMsg(null);
        if (!validateForm()) return;
        handleUpdate();
    };

    const handleUpdate = async () => {
        setBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.post(`${uRLs.AUTH_URL}update-password`, isResetData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
            });
            showAlert("success", Messages.SUCCESS.PASSWORD_CHANGED);
            navigate(`/${successRedirect}`, { replace: true, state: null });
        } catch (error) {
            if (error.response?.data?.code === 'TOKEN_EXPIRED') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else if (error.response?.data?.code === 'FORBIDDEN') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setBtnLoading(false);
        }
    };

    useEffect(() => {
        if (!accessToken || !successRedirect || !failureRedirect) {
            navigate("/" + failureRedirect + "");
        }
    }, [accessToken, navigate]);


    return (
        <div class="wrapper">
            <div class="authentication-reset-password d-flex align-items-center justify-content-center">
                <div class="container">
                    <div class="row row-cols-1 row-cols-lg-2 row-cols-xl-3">
                        <div class="col mx-auto">
                            <div class="card">
                                <div className="card-body">
                                    <div class="p-4">
                                        <div class="mb-4 text-center">
                                            <img src="/assets/images/logo-icon.png" width="60" alt="" />
                                        </div>
                                        <div class="text-start mb-4">
                                            <h4 className="text-left mt-3 fw-400">Reset Password 🔒</h4>
                                            <p className="text-left mb-0">Your new password must be different from previously used passwords</p>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label htmlFor="password" className="form-label">
                                                    Password <span className="text-danger"> *</span>
                                                </label>
                                                <div className="input-group">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control"
                                                        name="password"
                                                        autoComplete="off"
                                                        value={isResetData.password}
                                                        onChange={handleChange}
                                                        onKeyDown={handleKeyDown}
                                                        ref={passwordInputRef}
                                                    />
                                                    <span
                                                        className="input-group-text bg-transparent"
                                                        onClick={togglePasswordVisibility}>
                                                        <i className={`bx ${showPassword ? "bx-show" : "bx-hide"}`} ></i>
                                                    </span>
                                                </div>
                                                {error.password && (<div className="invalid-feedback">{error.password}</div>)}
                                            </div>
                                            <div className="col-12">
                                                <label htmlFor="repassword" className="form-label">
                                                    Confirm Password <span className="text-danger"> *</span>
                                                </label>
                                                <div className="input-group">
                                                    <input
                                                        type={showRePassword ? "text" : "password"}
                                                        className="form-control"
                                                        name="repassword"
                                                        autoComplete="off"
                                                        value={isResetData.repassword}
                                                        onChange={handleChange}
                                                        onKeyDown={handleKeyDown}
                                                        ref={repasswordInputRef}
                                                    />
                                                    <span
                                                        className="input-group-text bg-transparent"
                                                        onClick={toggleRePasswordVisibility} >
                                                        <i className={`bx ${showRePassword ? "bx-show" : "bx-hide"}`} ></i>
                                                    </span>
                                                </div>
                                                {error.repassword && (<div className="invalid-feedback">{error.repassword}</div>)}
                                            </div>

                                            {errorMsg && <div className="invalid-feedback">{errorMsg}</div>}

                                            <div className="col-12">
                                                <div className="d-grid">
                                                    {isBtnLoading ? (
                                                        <button className="btn btn-primary pb-0" disabled>
                                                            <CircularProgress size={22} className="text-white" />
                                                        </button>
                                                    ) : (
                                                        <button type="button" onClick={handleReset} className="btn btn-primary">
                                                            Set New Password
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div class="text-center">
                                                    <Link to="/login" class="d-flex align-items-center justify-content-center">
                                                        <ChevronLeftIcon iClass={"me-1 fs-18"}/>
                                                        Back to Login
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdatePassword;